import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import { concat, filter, includes, some, uniqBy } from 'lodash';
import { asyncSimpleModal, getDifferenceList } from '../../lib/common';
import { appActions } from '../../store/modules/app';
import { couponActions } from '../../store/modules/coupon';
import { COUPON_GENERATE_USER_FILTER_OPTION_MODEL } from '../../store/modules/coupon/models';
import { LIST_PAGINATION_MODEL } from '../../store/modules/app/models';
import {
  MAXIMUM_NUMBER_OF_COUPON_GENERATE_USER,
  MAXIMUM_NUMBER_OF_COUPON_KIND,
} from '../../constants/service/app/couponConstants';

export const useCouponGenerate = (couponId?: number) => {
  const couponGenerate = useSelector((state: any) => state.coupon.couponGenerate);
  const dispatch = useDispatch();

  useEffect(() => {
    if (couponId) {
      dispatch(
        couponActions.getPreparationGenerateCoupon({
          couponId,
          filterOption: COUPON_GENERATE_USER_FILTER_OPTION_MODEL,
          pagination: LIST_PAGINATION_MODEL,
        }),
      );
      dispatch(
        couponActions.setData({
          path: ['couponGenerate', 'issuedUser', 'selectedUserInfoList'],
          value: [],
        }),
      );
    }
  }, [couponId, dispatch]);

  const getList = useCallback(
    (data: Record<string, unknown>) => {
      const { filterOption, pagination } = couponGenerate.issuedUser.userList;
      dispatch(
        couponActions.getGenerateCouponUserList({
          filterOption,
          pagination,
          ...data,
        }),
      );
    },
    [couponGenerate.issuedUser.userList, dispatch],
  );

  const onSubmit = useCallback(
    (form: any) => {
      const { couponList, issuedUser } = couponGenerate;
      const { selectedUserIdList } = issuedUser;
      const { selectedCouponInfoList, selectedCouponIdList } = couponList;

      form.validateFieldsAndScroll({ scroll: { offsetTop: 100, offsetBottom: 100 } }, async (err: any) => {
        if (err) return;

        if (!selectedCouponIdList.length) {
          message.error('선택된 쿠폰이 없습니다.');
          return;
        }
        if (!selectedUserIdList.length) {
          message.error('선택된 유저가 없습니다.');
          return;
        }

        const confirmResult = await asyncSimpleModal({
          type: 'confirm',
          title: '쿠폰 발급하기',
          message: `${selectedCouponIdList.length} 쿠폰을 ${selectedUserIdList.length}명에게 발급하시겠습니까?`,
        });
        if (!confirmResult) return;

        dispatch(
          appActions.checkValidGenerateCoupon({
            selectedUserIdList,
            selectedCouponInfoList,
            targetAction: couponActions,
          }),
        );
      });
    },
    [couponGenerate, dispatch],
  );

  const onCheckIssuedCoupon = useCallback(
    async (list: number[]) => {
      const { couponList } = couponGenerate;
      const { selectedCouponInfoList, issuedCouponList } = couponList;

      if (list.length > MAXIMUM_NUMBER_OF_COUPON_KIND) {
        await asyncSimpleModal({
          type: 'error',
          title: '엑셀 일괄 발급 권장',
          message: `발급할 쿠폰 종류가 너무 많습니다.\n ${MAXIMUM_NUMBER_OF_COUPON_KIND}건 이상은 엑셀 일괄발급을 이용해 주세요.`,
        });
        return;
      }

      let targetList: any[] = [];

      const addCouponIdList = getDifferenceList(list, selectedCouponInfoList, null, 'id');
      if (addCouponIdList.length) {
        const addCouponInfoList = filter(issuedCouponList, (item: any) =>
          some(addCouponIdList, (id: any) => id === item.id),
        );
        targetList = concat(selectedCouponInfoList, addCouponInfoList);
      }

      const subCouponIdList = getDifferenceList(selectedCouponInfoList, list, 'id', null);
      if (subCouponIdList.length) {
        targetList = filter(selectedCouponInfoList, (item: any) =>
          !some(subCouponIdList, (_item: any) => _item.id === item.id),
        );
      }

      targetList = uniqBy(targetList, 'id');

      dispatch(
        couponActions.setData({
          path: ['couponGenerate', 'couponList', 'selectedCouponInfoList'],
          value: targetList,
        }),
      );
      dispatch(
        couponActions.setData({
          path: ['couponGenerate', 'couponList', 'selectedCouponIdList'],
          value: list,
        }),
      );
    },
    [couponGenerate, dispatch],
  );

  const onCheckUser = useCallback(
    async (list: number[]) => {
      const { issuedUser } = couponGenerate;
      const { userList, selectedUserInfoList } = issuedUser;

      if (list.length > MAXIMUM_NUMBER_OF_COUPON_GENERATE_USER) {
        await asyncSimpleModal({
          type: 'error',
          title: '엑셀 일괄 발급 권장',
          message: `발급할 유저가 너무 많습니다.\n ${MAXIMUM_NUMBER_OF_COUPON_GENERATE_USER}건 이상은 엑셀 일괄발급을 이용해 주세요.`,
        });
        return;
      }

      let targetList: any[] = [];

      const addUserIdList = getDifferenceList(list, selectedUserInfoList, null, 'id');
      if (addUserIdList.length) {
        const addUserInfoList = filter(userList.list, (item: any) =>
          some(addUserIdList, (id: any) => id === item.id),
        );
        targetList = concat(selectedUserInfoList, addUserInfoList);
      }

      const subUserIdList = getDifferenceList(selectedUserInfoList, list, 'id', null);
      if (subUserIdList.length) {
        targetList = filter(selectedUserInfoList, (item: any) =>
          !some(subUserIdList, (_item: any) => _item.id === item.id),
        );
      }

      targetList = uniqBy(targetList, 'id');

      dispatch(
        couponActions.setData({
          path: ['couponGenerate', 'issuedUser', 'selectedUserInfoList'],
          value: targetList,
        }),
      );
      dispatch(
        couponActions.setData({
          path: ['couponGenerate', 'issuedUser', 'selectedUserIdList'],
          value: list,
        }),
      );
    },
    [couponGenerate, dispatch],
  );

  const deleteSelectedIssuedCoupon = useCallback(() => {
    const { issuedCouponList, selectedCouponIdList } = couponGenerate.couponList;

    dispatch(
      couponActions.setData({
        path: ['couponGenerate', 'couponList', 'issuedCouponList'],
        value: filter(issuedCouponList, (item: any) => !includes(selectedCouponIdList, item.id)),
      }),
    );
    dispatch(
      couponActions.setData({
        path: ['couponGenerate', 'couponList', 'selectedCouponIdList'],
        value: [],
      }),
    );
    dispatch(
      couponActions.setData({
        path: ['couponGenerate', 'couponList', 'selectedCouponInfoList'],
        value: [],
      }),
    );
  }, [couponGenerate.couponList, dispatch]);

  const getExcelDownload = useCallback(() => {
    const { filterOption } = couponGenerate.issuedUser.userList;
    dispatch(couponActions.getGenerateCouponUserListExcelDownload({ filterOption }));
  }, [couponGenerate.issuedUser.userList, dispatch]);

  const setData = useCallback(
    (payload: any) => dispatch(couponActions.setData(payload)),
    [dispatch],
  );

  const toggleModal = useCallback(
    (payload: any) => dispatch(couponActions.toggleModal(payload)),
    [dispatch],
  );

  return {
    couponGenerate,
    setData,
    toggleModal,
    onCheckIssuedCoupon,
    deleteSelectedIssuedCoupon,
    onCheckUser,
    onSubmit,
    getList,
    getExcelDownload,
  };
};
